import { useState, useCallback, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument, rgb } from "pdf-lib";
import {
  FileText,
  Signature,
  Upload,
  ChevronLeft,
  ChevronRight,
  ZoomOut,
  Download,
  X,
  Move,
  Trash2,
} from "lucide-react";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { connect } from "react-redux";
import CryptoJS from "crypto-js";
import { toast } from "react-toastify";
// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
function SignaturePage(props) {
  const [pdfInfo, setPdfInfo] = useState(null);
  const [placedImages, setPlacedImages] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.4000000000000004);
  const [draggedImage, setDraggedImage] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizing, setResizing] = useState(null);
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const fileUrl = queryParams.get("file");
  const sequenceNos = queryParams.get("sequenceNo");
  const referenceNos = queryParams.get("referenceNo");
  const filenameNos = queryParams.get("filename");
  const locationNos = queryParams.get("location");
  const toPathPdf = async (fileUrl, sequenceNos) => {
    const isFileValid = fileUrl && fileUrl !== "null" && fileUrl.trim() !== "";
    const url =
      process.env.REACT_APP_WEB_SERVICE_URL +
      (isFileValid
        ? `/api/Attachment/DownloadFromAzure?FilePath=${encodeURIComponent(
          fileUrl
        )}`
        : `/api/Attachment/DownloadFile?SequenceNo=${sequenceNos}`);

    try {
      const fileResponse = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: "Bearer " + props.getToken,
        },
      });

      if (!fileResponse.ok) {
        throw new Error(`Failed to fetch PDF: ${fileResponse.status}`);
      }
      const blob = await fileResponse.blob();
      const objectUrl = URL.createObjectURL(blob);
      handlePDFUpload(objectUrl);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  };

  useEffect(() => {
    if (fileUrl || sequenceNos) {
      toPathPdf(fileUrl, sequenceNos);
    }
  }, [fileUrl, sequenceNos]);

  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [pdfPageSize, setPdfPageSize] = useState({ width: 0, height: 0 });

  const pdfContainerRef = useRef(null);

  const handlePDFUpload = useCallback((fileOrUrl) => {
    if (typeof fileOrUrl === "string") {
      setPdfInfo({ file: null, url: fileOrUrl, numPages: 0 });
    } else if (fileOrUrl instanceof File) {
      const url = URL.createObjectURL(fileOrUrl);
      setPdfInfo({ file: fileOrUrl, url, numPages: 0 });
    }

    setPlacedImages([]);
    setCurrentPage(1);
  }, []);

  const handlePDFLoad = useCallback(
    ({ numPages }) => {
      if (pdfInfo) {
        setPdfInfo((prev) => (prev ? { ...prev, numPages } : null));
      }
    },
    [pdfInfo]
  );
  const handlePageRenderSuccess = useCallback((page) => {
    const { originalWidth, originalHeight } = page;
    setPdfPageSize({ width: originalWidth, height: originalHeight });
  }, []);

  const handleImageUpload = useCallback((file) => {
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }
    uploadSignatureApi(file);
  }, []);

  const uploadSignatureApi = async (file) => {
    const formData = new FormData();
    formData.append("files", file, file.name);

    const response = await fetch(
      `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/AddSignature?UserId=${props.UserId}`,
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + props.getToken,
        },
        body: formData,
      }
    );
    if (response.ok) {
      uploadSignatureGet();
    }

    return await response.json();
  };

  function getInitials(name) {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0]?.toUpperCase() || "";
    const second = parts[1]?.[0]?.toUpperCase() || "";
    return first + second;
  }

  function dataURLtoBlob(dataURL) {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  const generateSignatureImage = async (initials) => {
    const fonts = [
      "italic 80px Georgia", // Elegant serif italic

      "italic 80px Calibri", // Popular modern sans
      "80px Franklin Gothic Medium", // Strong sans-serif
    ];

    for (let i = 0; i < fonts.length; i++) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = 400;
      canvas.height = 200;

      // Background white
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Text style
      ctx.fillStyle = "black";
      if (i === 2) {
        ctx.fillStyle = "#0083BD";
      }
      if (i === 1) {
        ctx.fillStyle = "#3026E0";
      }
      ctx.font = fonts[i];
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Shadow effect
      ctx.shadowColor = "rgba(0, 0, 0, 0.2)";
      ctx.shadowBlur = 5;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;

      // Draw initials
      ctx.fillText(initials, canvas.width / 2, canvas.height / 2 - 10);

      // Reset shadow + underline line
      ctx.shadowColor = "transparent";
      ctx.strokeStyle = "black";
      if (i === 1 || 2) {
        ctx.lineWidth = 4;
      }

      ctx.beginPath();

      if (i === 2) {
        // horizantal top
        ctx.moveTo(canvas.width * 0.2, canvas.height * 0.2);
        ctx.lineTo(canvas.width * 0.8, canvas.height * 0.2);
      }
      if (i === 1) {
        // horizantal end
        ctx.moveTo(canvas.width * 0.2, canvas.height * 0.75);

        ctx.lineTo(canvas.width * 0.8, canvas.height * 0.75);
      }
      ctx.stroke();

      // Convert to File and upload
      const dataUrl = canvas.toDataURL("image/png");
      const blob = dataURLtoBlob(dataUrl);
      const file = new File([blob], `signature_${i + 1}.png`, {
        type: "image/png",
      });

      await uploadSignatureApi(file);
    }
  };
  const signHereImage = async () => {
    const imageUrl = `${process.env.REACT_APP_ENV}/assets/images/icons/action/Sign.png`;
    const res = await fetch(imageUrl);
    const blob = await res.blob();
    const file = new File([blob], "signature_placeholder.png", {
      type: "image/png",
    });

    await uploadSignatureApi(file);

    // return await uploadSignatureGet();
  };
  const uploadSignatureGet = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/GetSignature?UserId=${props.UserId}`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + props.getToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();

      const signatureArray = Array.isArray(data)
        ? data
        : data?.resultObject || [];

      if (!data?.resultObject || data.resultObject.length === 0) {
        const initials = getInitials(props.userName);
        const file = await generateSignatureImage(initials);

        // signHereImage();
        await uploadSignatureApi({ file });
      }
      const newFilesPromises = signatureArray.map(async (item) => {
        const url =
          process.env.REACT_APP_WEB_SERVICE_URL +
          `/api/Attachment/${item?.filePath
            ? `DownloadFromAzure?FilePath=${encodeURIComponent(
              item.filePath
            )}`
            : `DownloadFile?SequenceNo=${item?.sequenceNo}`
          }`;

        const fileResponse = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: "Bearer " + props.getToken,
          },
        });
        if (!fileResponse.ok) {
          throw new Error(`Download failed: ${fileResponse.status}`);
        }

        const blob = await fileResponse.blob();

        const fileObj = new File([blob], item.filename, {
          type: item.filetype,
        });
        return {
          file: fileObj,
          width: item?.width || 120,
          height: item?.height || 60,
          name: item.filename,
          type: item.filetype,
          sequenceNo: item.sequenceNo,
        };
      });

      const newFiles = await Promise.all(newFilesPromises);
      setUploadedImages(newFiles);
    } catch (error) {
      console.error("Error fetching signature:", error);
    }
  };

  useEffect(() => {
    uploadSignatureGet();
  }, []);
  const fetchImageAsFile = async (url, fileName) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], fileName, { type: blob.type });
  };

  const addImageToPDF = useCallback(
    (file, width, height) => {
      const now = new Date();
      const formated = now.toLocaleDateString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const newImage = {
        id: `img-${Date.now()}-${Math.random()}`,
        file,
        url: URL.createObjectURL(file),
        x: 50,
        y: 50,
        width: width,
        height: height,
        pageNumber: currentPage,
        userName: file.name !== "signature_h.png" && props.userName,

        userType: file.name !== "signature_h.png" && props.designationName,
        timestamp: file.name !== "signature_h.png" && formated,
      };

      setPlacedImages((prev) => [...prev, newImage]);
    },
    [currentPage]
  );

  const removeUploadedImage = useCallback(
    async (index, toDelete) => {
      if (toDelete?.sequenceNo) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/DeleteSignature?UserId=${props.UserId}&SequenceNo=${toDelete?.sequenceNo}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + props.getToken,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const data = await response.json();

          setUploadedImages((prev) => prev.filter((_, i) => i !== index));
          return data;
        } catch (err) {
          console.error("Error deleting signature:", err);
          throw err;
        }
      }
    },
    [props.UserId, props.getToken]
  );

  const handleMouseDown = useCallback(
    (e, imageId, action) => {
      e.preventDefault();
      e.stopPropagation();

      const image = placedImages.find((img) => img.id === imageId);
      if (!image) return;

      if (action === "drag") {
        setDraggedImage(imageId);
        const imageElement = e.currentTarget;
        const rect = imageElement.getBoundingClientRect();
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      } else if (action === "resize") {
        setResizing(imageId);
        setResizeStart({
          x: e.clientX,
          y: e.clientY,
          width: image.width * scale,
          height: image.height * scale,
        });
      }
    },
    [placedImages, scale]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (draggedImage && pdfContainerRef.current) {
        const containerRect = pdfContainerRef.current.getBoundingClientRect();
        const x = (e.clientX - containerRect.left - dragOffset.x) / scale;
        const y = (e.clientY - containerRect.top - dragOffset.y) / scale;

        setPlacedImages((prev) =>
          prev.map((img) =>
            img.id === draggedImage
              ? { ...img, x: Math.max(0, x), y: Math.max(0, y) }
              : img
          )
        );
      } else if (resizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        const newWidth = Math.max(20, (resizeStart.width + deltaX) / scale);
        const newHeight = Math.max(20, (resizeStart.height + deltaY) / scale);

        setPlacedImages((prev) =>
          prev.map((img) =>
            img.id === resizing
              ? { ...img, width: newWidth, height: newHeight }
              : img
          )
        );
      }
    },
    [draggedImage, resizing, dragOffset, scale, resizeStart]
  );

  const handleMouseUp = useCallback(() => {
    setDraggedImage(null);
    setResizing(null);
  }, []);

  useEffect(() => {
    if (draggedImage || resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [draggedImage, resizing, handleMouseMove, handleMouseUp]);

  const removePlacedImage = useCallback((id) => {
    setPlacedImages((prev) => prev.filter((img) => img.id !== id));
  }, []);

  // const savePDFWithImages = useCallback(
  //   async (type) => {
  //     if (!pdfInfo) {
  //       alert("No PDF loaded");
  //       return;
  //     }

  //     try {
  //       if (placedImages.length === 0) {
  //         let existingPdfBytes;

  //         if (pdfInfo.file) {
  //           existingPdfBytes = await pdfInfo.file.arrayBuffer();
  //         } else if (pdfInfo.url) {
  //           const response = await fetch(pdfInfo.url);
  //           existingPdfBytes = await response.arrayBuffer();
  //         } else {
  //           throw new Error("No valid PDF source available");
  //         }

  //         const blob = new Blob([existingPdfBytes], {
  //           type: "application/pdf",
  //         });

  //         const fileName = pdfInfo.file?.name
  //           ? `${pdfInfo.file.name}`
  //           : filenameNos;
  //         const file = new File([blob], filenameNos, {
  //           type: "application/pdf",
  //         });
  //         if (type === "API") {
  //           try {
  //             const formData = new FormData();
  //             formData.append("files", file);

  //             // --- Upload API ---
  //             const uploadResponse = await fetch(
  //               `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/UploadAttachments?ReferenceNo=${referenceNos}&UserId=${props.UserId}&Remarks=`,
  //               {
  //                 method: "POST",
  //                 headers: {
  //                   Authorization: "Bearer " + props.getToken,
  //                 },
  //                 body: formData,
  //               }
  //             );

  //             // ✅ Check if upload succeeded
  //             if (!uploadResponse.ok) {
  //               throw new Error(
  //                 "Upload API failed with status " + uploadResponse.status
  //               );
  //             }

  //             const uploadData = await uploadResponse.json();


  //             // --- Proceed only if upload succeeded ---
  //             if (uploadData.resultObject === 0) {
  //               console.log(
  //                 "uploadData firstr ",
  //                 uploadData.resultObject === 0
  //               );

  //               // --- Delete API ---
  //               const deleteUrl = `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/DeleteAttachments?ReferenceNo=${referenceNos}&SequenceNo=${sequenceNos}`;

  //               const deleteResponse = await fetch(deleteUrl, {
  //                 method: "POST",
  //                 headers: {
  //                   Authorization: "Bearer " + props.getToken,
  //                 },
  //               });

  //               if (!deleteResponse.ok) {
  //                 throw new Error(
  //                   "Delete API failed with status " + deleteResponse.status
  //                 );
  //               }

  //               const deleteResData = await deleteResponse.json();

  //               if (deleteResData.resultObject === 0) {
  //                 console.log("dlete second ", uploadData.resultObject === 0);
  //                 // --- Redirects based on location ---
  //                 const encryptedRef = encodeURI(
  //                   CryptoJS.AES.encrypt(
  //                     JSON.stringify(referenceNos),
  //                     process.env.REACT_APP_ENCRYPT_PASSWORD
  //                   ).toString()
  //                 );

  //                 switch (locationNos) {
  //                   case "/createrequest":
  //                   case "/draft/edit":
  //                     window.location = `${process.env.REACT_APP_ENV}/draft/edit?p=${encryptedRef}`;
  //                     break;

  //                   case "/pending/view":
  //                     window.open(`${locationNos}?p=${referenceNos}`, "_self");
  //                     break;

  //                   case "/mytask/pending/view":
  //                     window.location = `${process.env.REACT_APP_ENV}/mytask/pending/view?p=${encryptedRef}`;
  //                     break;

  //                   default:
  //                     console.warn("Unknown location:", locationNos);
  //                     break;
  //                 }
  //               } else {
  //                 toast.error("Delete failed. Please try again.", {
  //                   position: toast.POSITION.TOP_CENTER,
  //                   autoClose: 2000,
  //                 });
  //               }
  //             } else {
  //               toast.error("Upload failed. Please try again.", {
  //                 position: toast.POSITION.TOP_CENTER,
  //                 autoClose: 2000,
  //               });
  //             }
  //           } catch (error) {
  //             console.error("Error:", error);
  //             toast.error("Something went wrong! Please try again.", {
  //               position: toast.POSITION.TOP_CENTER,
  //               autoClose: 2000,
  //             });
  //           }
  //         } else {
  //           // --- Local download ---
  //           const url = URL.createObjectURL(file);
  //           const link = document.createElement("a");
  //           link.href = url;
  //           link.download = fileName;
  //           document.body.appendChild(link);
  //           link.click();
  //           document.body.removeChild(link);
  //           URL.revokeObjectURL(url);
  //         }

  //         // if (type === "API") {
  //         //   const formData = new FormData();
  //         //   formData.append("files", file);
  //         //  debugger
  //         //   const uploadResponse = await fetch(
  //         //     `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/UploadAttachments?ReferenceNo=${referenceNos}&UserId=${props.UserId}&Remarks=`,
  //         //     {
  //         //       method: "POST",
  //         //       headers: {
  //         //         Authorization: "Bearer " + props.getToken,
  //         //       },
  //         //       body: formData,
  //         //     }
  //         //   );
  //         //   const uploadData = await uploadResponse.json();
  //         //   if (uploadData.resultObject == 0) {
  //         //     const deleteUrl = `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/DeleteAttachments?ReferenceNo=${referenceNos}&SequenceNo=${sequenceNos}`;
  //         //     const deleteResponse = await fetch(deleteUrl, {
  //         //       method: "POST",
  //         //       headers: {
  //         //         Authorization: "Bearer " + props.getToken,
  //         //       },
  //         //     });
  //         //     const deleteResData = await deleteResponse.json();

  //         //     if (deleteResData.resultObject == 0) {
  //         //       if (locationNos == "/createrequest") {
  //         //         // const encrypted = encodeURI(
  //         //         //   CryptoJS.AES.encrypt(
  //         //         //     JSON.stringify(referenceNos),
  //         //         //     process.env.REACT_APP_ENCRYPT_PASSWORD
  //         //         //   ).toString()
  //         //         // );
  //         //         window.location =
  //         //           process.env.REACT_APP_ENV +
  //         //           "/draft/edit?p=" +
  //         //           encodeURI(
  //         //             CryptoJS.AES.encrypt(
  //         //               JSON.stringify(referenceNos),
  //         //               process.env.REACT_APP_ENCRYPT_PASSWORD
  //         //             ).toString()
  //         //           );
  //         //         // window.location = `${process.env.REACT_APP_WEB_SERVICE_URL}/draft/edit?p=${encrypted}`;
  //         //       } else if (locationNos == "/pending/view") {
  //         //         window.open(`${locationNos}?p=${referenceNos}`, "_self");
  //         //       } else if (locationNos == "/mytask/pending/view") {
  //         //         window.location =
  //         //           process.env.REACT_APP_ENV +
  //         //           "/mytask/pending/view?p=" +
  //         //           encodeURI(
  //         //             CryptoJS.AES.encrypt(
  //         //               JSON.stringify(referenceNos),
  //         //               process.env.REACT_APP_ENCRYPT_PASSWORD
  //         //             ).toString()
  //         //           );
  //         //       } else if (locationNos == "/draft/edit") {
  //         //         // const encrypted = encodeURI(
  //         //         //   CryptoJS.AES.encrypt(
  //         //         //     JSON.stringify(referenceNos),
  //         //         //     process.env.REACT_APP_ENCRYPT_PASSWORD
  //         //         //   ).toString()
  //         //         // );

  //         //         // window.location = `${process.env.REACT_APP_WEB_SERVICE_URL}/draft/edit?p=${encrypted}`;
  //         //         window.location =
  //         //           process.env.REACT_APP_ENV +
  //         //           "/draft/edit?p=" +
  //         //           encodeURI(
  //         //             CryptoJS.AES.encrypt(
  //         //               JSON.stringify(referenceNos),
  //         //               process.env.REACT_APP_ENCRYPT_PASSWORD
  //         //             ).toString()
  //         //           );
  //         //       }
  //         //     }
  //         //   } else {
  //         //     toast.error("Something went wrong! please try again", {
  //         //       position: toast.POSITION.TOP_CENTER,
  //         //       autoClose: 2000,
  //         //     });
  //         //   }
  //         // } else {
  //         //   const url = URL.createObjectURL(file);
  //         //   const link = document.createElement("a");
  //         //   link.href = url;
  //         //   link.download = fileName;
  //         //   document.body.appendChild(link);
  //         //   link.click();
  //         //   document.body.removeChild(link);
  //         //   URL.revokeObjectURL(url);
  //         // }
  //         return;
  //       }

  //       let existingPdfBytes;
  //       if (pdfInfo.file) {
  //         existingPdfBytes = await pdfInfo.file.arrayBuffer();
  //       } else if (pdfInfo.url) {
  //         const response = await fetch(pdfInfo.url);
  //         existingPdfBytes = await response.arrayBuffer();
  //       } else {
  //         throw new Error("No valid PDF source available");
  //       }

  //       const pdfDoc = await PDFDocument.load(existingPdfBytes);
  //       const pages = pdfDoc.getPages();

  //       const imagesByPage = placedImages.reduce((acc, img) => {
  //         if (!acc[img.pageNumber]) acc[img.pageNumber] = [];
  //         acc[img.pageNumber].push(img);
  //         return acc;
  //       }, {});

  //       for (const [pageNumStr, pageImages] of Object.entries(imagesByPage)) {
  //         const pageNum = parseInt(pageNumStr);
  //         const pageIndex = pageNum - 1;

  //         if (pageIndex >= 0 && pageIndex < pages.length) {
  //           const page = pages[pageIndex];
  //           const { width: pdfPageWidth, height: pdfPageHeight } =
  //             page.getSize();

  //           for (const img of pageImages) {
  //             try {
  //               const viewportToPdfScaleX = pdfPageWidth / pdfPageSize.width;
  //               const viewportToPdfScaleY = pdfPageHeight / pdfPageSize.height;

  //               const pdfX = img.x * viewportToPdfScaleX;
  //               const pdfY =
  //                 (pdfPageSize.height - img.y - img.height) *
  //                 viewportToPdfScaleY;
  //               const pdfWidth = img.width * viewportToPdfScaleX;
  //               const pdfHeight = img.height * viewportToPdfScaleY;

  //               const imageBytes = await img.file.arrayBuffer();
  //               let embeddedImage;

  //               if (img.file.type.includes("png")) {
  //                 embeddedImage = await pdfDoc.embedPng(imageBytes);
  //               } else {
  //                 embeddedImage = await pdfDoc.embedJpg(imageBytes);
  //               }

  //               page.drawImage(embeddedImage, {
  //                 x: pdfX - 1,
  //                 y: pdfY - 5,
  //                 width: pdfWidth,
  //                 height: pdfHeight,
  //               });
  //               page.drawImage(embeddedImage, {
  //                 x: pdfX - 1,
  //                 y: pdfY - 5,
  //                 width: pdfWidth,
  //                 height: pdfHeight,
  //               });
  //               if (img.timestamp) {
  //                 const textX = pdfX - 1;
  //                 const textY = pdfY - 15; // adjust spacing below image

  //                 page.drawText(`${img.userName}`, {
  //                   x: textX,
  //                   y: textY,
  //                   size: 8,
  //                   color: rgb(0, 0, 0),
  //                 });

  //                 page.drawText(`${img.userType}`, {
  //                   x: textX,
  //                   moveTo: 20,
  //                   y: textY - 10,
  //                   size: 8,
  //                   color: rgb(0, 0, 0),
  //                 });

  //                 page.drawText(`Signed at: ${img.timestamp}`, {
  //                   x: textX,
  //                   y: textY - 20,
  //                   size: 8,
  //                   color: rgb(0, 0, 0),
  //                 });
  //               }
  //             } catch (error) {
  //               console.error(`Error embedding image ${img.file.name}:`, error);
  //               alert(
  //                 `Error embedding image ${img.file.name}. Please try again.`
  //               );
  //             }
  //           }
  //         }
  //       }

  //       const pdfBytes = await pdfDoc.save();
  //       const blob = new Blob([pdfBytes], { type: "application/pdf" });

  //       const fileName = pdfInfo.file?.name
  //         ? `${pdfInfo.file.name}`
  //         : filenameNos;
  //       const file = new File([blob], filenameNos, { type: "application/pdf" });
  //       if (type === "API") {
  //         try {
  //           const formData = new FormData();
  //           formData.append("files", file);

  //           // --- Upload API ---
  //           const uploadResponse = await fetch(
  //             `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/UploadAttachments?ReferenceNo=${referenceNos}&UserId=${props.UserId}&Remarks=`,
  //             {
  //               method: "POST",
  //               headers: {
  //                 Authorization: "Bearer " + props.getToken,
  //               },
  //               body: formData,
  //             }
  //           );

  //           // ✅ Check if upload succeeded
  //           if (!uploadResponse.ok) {
  //             throw new Error(
  //               "Upload API failed with status " + uploadResponse.status
  //             );
  //           }

  //           const uploadData = await uploadResponse.json();

  //           // --- Proceed only if upload succeeded ---
  //           if (uploadData.resultObject === 0) {
  //             console.log("uploadData firstr ", uploadData.resultObject === 0);
  //             // --- Delete API ---
  //             const deleteUrl = `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/DeleteAttachments?ReferenceNo=${referenceNos}&SequenceNo=${sequenceNos}`;

  //             const deleteResponse = await fetch(deleteUrl, {
  //               method: "POST",
  //               headers: {
  //                 Authorization: "Bearer " + props.getToken,
  //               },
  //             });

  //             if (!deleteResponse.ok) {
  //               throw new Error(
  //                 "Delete API failed with status " + deleteResponse.status
  //               );
  //             }

  //             const deleteResData = await deleteResponse.json();

  //             if (deleteResData.resultObject === 0) {
  //               console.log("dlete second ", uploadData.resultObject === 0);
  //               // --- Redirects based on location ---
  //               const encryptedRef = encodeURI(
  //                 CryptoJS.AES.encrypt(
  //                   JSON.stringify(referenceNos),
  //                   process.env.REACT_APP_ENCRYPT_PASSWORD
  //                 ).toString()
  //               );

  //               switch (locationNos) {
  //                 case "/createrequest":
  //                 case "/draft/edit":
  //                   window.location = `${process.env.REACT_APP_ENV}/draft/edit?p=${encryptedRef}`;
  //                   break;

  //                 case "/pending/view":
  //                   window.open(`${locationNos}?p=${referenceNos}`, "_self");
  //                   break;

  //                 case "/mytask/pending/view":
  //                   window.location = `${process.env.REACT_APP_ENV}/mytask/pending/view?p=${encryptedRef}`;
  //                   break;

  //                 default:
  //                   console.warn("Unknown location:", locationNos);
  //                   break;
  //               }
  //             } else {
  //               toast.error("Delete failed. Please try again.", {
  //                 position: toast.POSITION.TOP_CENTER,
  //                 autoClose: 2000,
  //               });
  //             }
  //           } else {
  //             toast.error("Upload failed. Please try again.", {
  //               position: toast.POSITION.TOP_CENTER,
  //               autoClose: 2000,
  //             });
  //           }
  //         } catch (error) {
  //           console.error("Error:", error);
  //           toast.error("Something went wrong! Please try again.", {
  //             position: toast.POSITION.TOP_CENTER,
  //             autoClose: 2000,
  //           });
  //         }
  //       } else {
  //         // --- Local download ---
  //         const url = URL.createObjectURL(file);
  //         const link = document.createElement("a");
  //         link.href = url;
  //         link.download = fileName;
  //         document.body.appendChild(link);
  //         link.click();
  //         document.body.removeChild(link);
  //         URL.revokeObjectURL(url);
  //       }

  //       // if (type === "API") {
  //       //   const formData = new FormData();

  //       //   formData.append("files", file);

  //       //   const uploadResponse = await fetch(
  //       //     `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/UploadAttachments?ReferenceNo=${referenceNos}&UserId=${props.UserId}&Remarks=`,
  //       //     {
  //       //       method: "POST",
  //       //       headers: {
  //       //         Authorization: "Bearer " + props.getToken,
  //       //       },
  //       //       body: formData,
  //       //     }
  //       //   );
  //       //   const uploadData = await uploadResponse.json();

  //       //   if (uploadData.resultObject == 0) {
  //       //     // window.open(`/pending/view?p=${referenceNos}`, "_self");
  //       //     const deleteUrl = `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/DeleteAttachments?ReferenceNo=${referenceNos}&SequenceNo=${sequenceNos}`;
  //       //     const deleteResponse = await fetch(deleteUrl, {
  //       //       method: "POST",
  //       //       headers: {
  //       //         Authorization: "Bearer " + props.getToken,
  //       //       },
  //       //     });
  //       //     const deleteResData = await deleteResponse.json();

  //       //     if (deleteResData.resultObject == 0) {
  //       //       if (locationNos == "/createrequest") {
  //       //         window.location =
  //       //           process.env.REACT_APP_ENV +
  //       //           "/draft/edit?p=" +
  //       //           encodeURI(
  //       //             CryptoJS.AES.encrypt(
  //       //               JSON.stringify(referenceNos),
  //       //               process.env.REACT_APP_ENCRYPT_PASSWORD
  //       //             ).toString()
  //       //           );
  //       //       } else if (locationNos == "/pending/view") {
  //       //         window.open(`${locationNos}?p=${referenceNos}`, "_self");
  //       //       } else if (locationNos == "/mytask/pending/view") {
  //       //         window.location =
  //       //           process.env.REACT_APP_ENV +
  //       //           "/mytask/pending/view?p=" +
  //       //           encodeURI(
  //       //             CryptoJS.AES.encrypt(
  //       //               JSON.stringify(referenceNos),
  //       //               process.env.REACT_APP_ENCRYPT_PASSWORD
  //       //             ).toString()
  //       //           );
  //       //       } else if (locationNos == "/draft/edit") {
  //       //         window.location =
  //       //           process.env.REACT_APP_ENV +
  //       //           "/draft/edit?p=" +
  //       //           encodeURI(
  //       //             CryptoJS.AES.encrypt(
  //       //               JSON.stringify(referenceNos),
  //       //               process.env.REACT_APP_ENCRYPT_PASSWORD
  //       //             ).toString()
  //       //           );
  //       //       }
  //       //     }
  //       //   } else {
  //       //     toast.error("Something went wrong! please try again", {
  //       //       position: toast.POSITION.TOP_CENTER,
  //       //       autoClose: 2000,
  //       //     });
  //       //   }
  //       // } else {
  //       //   // Download locally
  //       //   const url = URL.createObjectURL(file);
  //       //   const link = document.createElement("a");
  //       //   link.href = url;
  //       //   link.download = fileName;
  //       //   document.body.appendChild(link);
  //       //   link.click();
  //       //   document.body.removeChild(link);
  //       //   URL.revokeObjectURL(url);
  //       // }
  //     } catch (error) {
  //       console.error("Error saving PDF:", error);
  //       alert(`Error saving PDF: ${error.message || "Unknown error"}`);
  //     }
  //   },
  //   [pdfInfo, placedImages, pdfPageSize, scale]
  // );
  const savePDFWithImages = useCallback(
    async (type) => {
      if (!pdfInfo) {
        alert("No PDF loaded");
        return;
      }

      try {
        if (placedImages.length === 0) {
          let existingPdfBytes;

          if (pdfInfo.file) {
            existingPdfBytes = await pdfInfo.file.arrayBuffer();
          } else if (pdfInfo.url) {
            const response = await fetch(pdfInfo.url);
            existingPdfBytes = await response.arrayBuffer();
          } else {
            throw new Error("No valid PDF source available");
          }

          const blob = new Blob([existingPdfBytes], {
            type: "application/pdf",
          });

          const fileName = pdfInfo.file?.name
            ? `${pdfInfo.file.name}`
            : filenameNos;
          const file = new File([blob], filenameNos, {
            type: "application/pdf",
          });

          if (type === "API") {
            const formData = new FormData();
            console.log("hel ", type === "API");

            formData.append("files", file);

            await fetch(
              `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/UploadAttachments?ReferenceNo=${referenceNos}&UserId=${props.UserId}&Remarks=`,
              {
                method: "POST",
                headers: {
                  Authorization: "Bearer " + props.getToken,
                },
                body: formData,
              }
            ).then(async (res) => {
              console.log("😊--------------", res)

              if (res.ok) {
                console.log(res.ok, "upload ok down");

                // window.open(`/pending/view?p=${referenceNos}`, "_self");
                const deleteUrl = `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/DeleteAttachments?ReferenceNo=${referenceNos}&SequenceNo=${sequenceNos}`;
                await fetch(deleteUrl, {
                  method: "POST",
                  headers: {
                    Authorization: "Bearer " + props.getToken,
                  },
                }).then((deleteRes) => {
                  if (deleteRes.ok) {
                    console.log(deleteRes.ok, "delete ok down");
                    if (locationNos == "/createrequest") {
                      window.location =
                        process.env.REACT_APP_ENV +
                        "/draft/edit?p=" +
                        encodeURI(
                          CryptoJS.AES.encrypt(
                            JSON.stringify(referenceNos),
                            process.env.REACT_APP_ENCRYPT_PASSWORD
                          ).toString()
                        );
                    } else if (locationNos == "/pending/view") {
                      window.open(`${locationNos}?p=${referenceNos}`, "_self");
                    } else if (locationNos == "/mytask/pending/view") {
                      window.location =
                        process.env.REACT_APP_ENV +
                        "/mytask/pending/view?p=" +
                        encodeURI(
                          CryptoJS.AES.encrypt(
                            JSON.stringify(referenceNos),
                            process.env.REACT_APP_ENCRYPT_PASSWORD
                          ).toString()
                        );
                    } else if (locationNos == "/draft/edit") {
                      window.location =
                        process.env.REACT_APP_ENV +
                        "/draft/edit?p=" +
                        encodeURI(
                          CryptoJS.AES.encrypt(
                            JSON.stringify(referenceNos),
                            process.env.REACT_APP_ENCRYPT_PASSWORD
                          ).toString()
                        );
                    }
                  }
                });

              } else {
                toast.error("Something went wrong! please try again", {
                  position: toast.POSITION.TOP_CENTER,
                  autoClose: 2000,
                });
              }

            }


            );




          } else {
            // Download locally
            const url = URL.createObjectURL(file);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
          return;
        }

        let existingPdfBytes;
        if (pdfInfo.file) {
          existingPdfBytes = await pdfInfo.file.arrayBuffer();
        } else if (pdfInfo.url) {
          const response = await fetch(pdfInfo.url);
          existingPdfBytes = await response.arrayBuffer();
        } else {
          throw new Error("No valid PDF source available");
        }

        const pdfDoc = await PDFDocument.load(existingPdfBytes);
        const pages = pdfDoc.getPages();

        const imagesByPage = placedImages.reduce((acc, img) => {
          if (!acc[img.pageNumber]) acc[img.pageNumber] = [];
          acc[img.pageNumber].push(img);
          return acc;
        }, {});

        for (const [pageNumStr, pageImages] of Object.entries(imagesByPage)) {
          const pageNum = parseInt(pageNumStr);
          const pageIndex = pageNum - 1;

          if (pageIndex >= 0 && pageIndex < pages.length) {
            const page = pages[pageIndex];
            const { width: pdfPageWidth, height: pdfPageHeight } =
              page.getSize();

            for (const img of pageImages) {
              try {
                const viewportToPdfScaleX = pdfPageWidth / pdfPageSize.width;
                const viewportToPdfScaleY = pdfPageHeight / pdfPageSize.height;

                const pdfX = img.x * viewportToPdfScaleX;
                const pdfY =
                  (pdfPageSize.height - img.y - img.height) *
                  viewportToPdfScaleY;
                const pdfWidth = img.width * viewportToPdfScaleX;
                const pdfHeight = img.height * viewportToPdfScaleY;

                const imageBytes = await img.file.arrayBuffer();
                let embeddedImage;

                if (img.file.type.includes("png")) {
                  embeddedImage = await pdfDoc.embedPng(imageBytes);
                } else {
                  embeddedImage = await pdfDoc.embedJpg(imageBytes);
                }

                page.drawImage(embeddedImage, {
                  x: pdfX - 1,
                  y: pdfY - 5,
                  width: pdfWidth,
                  height: pdfHeight,
                });
                page.drawImage(embeddedImage, {
                  x: pdfX - 1,
                  y: pdfY - 5,
                  width: pdfWidth,
                  height: pdfHeight,
                });
                if (img.timestamp) {
                  const textX = pdfX - 1;
                  const textY = pdfY - 15; // adjust spacing below image

                  page.drawText(`${img.userName}`, {
                    x: textX,
                    y: textY,
                    size: 8,
                    color: rgb(0, 0, 0),
                  });

                  page.drawText(`${img.userType}`, {
                    x: textX,
                    moveTo: 20,
                    y: textY - 10,
                    size: 8,
                    color: rgb(0, 0, 0),
                  });

                  page.drawText(`Signed at: ${img.timestamp}`, {
                    x: textX,
                    y: textY - 20,
                    size: 8,
                    color: rgb(0, 0, 0),
                  });
                }
              } catch (error) {
                console.error(`Error embedding image ${img.file.name}:`, error);
                alert(
                  `Error embedding image ${img.file.name}. Please try again.`
                );
              }
            }
          }
        }

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: "application/pdf" });

        const fileName = pdfInfo.file?.name
          ? `${pdfInfo.file.name}`
          : filenameNos;
        const file = new File([blob], filenameNos, { type: "application/pdf" });

        if (type === "API") {
          const formData = new FormData();
          console.log("hel ", type === "API");

          formData.append("files", file);

          await fetch(
            `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/UploadAttachments?ReferenceNo=${referenceNos}&UserId=${props.UserId}&Remarks=`,
            {
              method: "POST",
              headers: {
                Authorization: "Bearer " + props.getToken,
              },
              body: formData,
            }
          ).then(async (res) => {
            console.log("😊--------------", res)

            if (res.ok) {
              console.log(res.ok, "upload ok down");

              // window.open(`/pending/view?p=${referenceNos}`, "_self");
              const deleteUrl = `${process.env.REACT_APP_WEB_SERVICE_URL}/api/eDoc/DeleteAttachments?ReferenceNo=${referenceNos}&SequenceNo=${sequenceNos}`;
              await fetch(deleteUrl, {
                method: "POST",
                headers: {
                  Authorization: "Bearer " + props.getToken,
                },
              }).then((deleteRes) => {
                if (deleteRes.ok) {
                  console.log(deleteRes.ok, "delete ok down");
                  if (locationNos == "/createrequest") {
                    window.location =
                      process.env.REACT_APP_ENV +
                      "/draft/edit?p=" +
                      encodeURI(
                        CryptoJS.AES.encrypt(
                          JSON.stringify(referenceNos),
                          process.env.REACT_APP_ENCRYPT_PASSWORD
                        ).toString()
                      );
                  } else if (locationNos == "/pending/view") {
                    window.open(`${locationNos}?p=${referenceNos}`, "_self");
                  } else if (locationNos == "/mytask/pending/view") {
                    window.location =
                      process.env.REACT_APP_ENV +
                      "/mytask/pending/view?p=" +
                      encodeURI(
                        CryptoJS.AES.encrypt(
                          JSON.stringify(referenceNos),
                          process.env.REACT_APP_ENCRYPT_PASSWORD
                        ).toString()
                      );
                  } else if (locationNos == "/draft/edit") {
                    window.location =
                      process.env.REACT_APP_ENV +
                      "/draft/edit?p=" +
                      encodeURI(
                        CryptoJS.AES.encrypt(
                          JSON.stringify(referenceNos),
                          process.env.REACT_APP_ENCRYPT_PASSWORD
                        ).toString()
                      );
                  }
                }
              });

            } else {
              toast.error("Something went wrong! please try again", {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
              });
            }

          }


          );




        } else {
          // Download locally
          const url = URL.createObjectURL(file);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Error saving PDF:", error);
        alert(`Error saving PDF: ${error.message || "Unknown error"}`);
      }
    },
    [pdfInfo, placedImages, pdfPageSize, scale]
  );
  const handleDrop = useCallback(
    (e, type) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        if (type === "pdf" && files[0].type === "application/pdf") {
          handlePDFUpload(files[0]);
        } else if (type === "image" && files[0].type.startsWith("image/")) {
          handleImageUpload(files[0]);
        }
      }
    },
    [handlePDFUpload, handleImageUpload]
  );

  const currentPageImages = placedImages.filter(
    (img) => img.pageNumber === currentPage
  );
  console.log(props, "props");

  return (
    <div className="gradient-bg">
      <div className="container-fluid py-4">
        <header className="text-center mb-4">
          <h1 className="display-4 fw-bold text-dark mb-2">
            eDoc Signature Editor
          </h1>
          <p className="lead text-muted">
            Upload a PDF, add images, and create your custom document
          </p>
        </header>

        {!pdfInfo ? (
          <>
            {referenceNos || sequenceNos || fileUrl ? (
              <div
                className="d-flex align-items-center justify-content-center bg-white rounded shadow"
                style={{ height: "400px" }}
              >
                <div className="text-center">
                  <div
                    className="loading-spinner border border-primary border-3"
                    style={{
                      width: "3rem",
                      height: "3rem",
                      borderTopColor: "transparent",
                    }}
                  ></div>
                  <p className="text-muted mt-3">Loading PDF...</p>
                </div>
              </div>
            ) : (
              <div className="row justify-content-center">
                <div className="col-lg-8">
                  <div
                    className="drop-zone rounded p-5 text-center bg-light"
                    onDrop={(e) => handleDrop(e, "pdf")}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() =>
                      document.getElementById("pdf-input")?.click()
                    }
                  >
                    <div className="d-flex flex-column align-items-center">
                      <FileText size={48} className="text-primary mb-3" />
                      <div>
                        <h5 className="fw-bold text-dark"> PDF Document</h5>
                        <p className="text-muted mt-2">
                          Drag and drop your PDF here, or click to browse
                        </p>
                      </div>
                    </div>
                    <input
                      id="pdf-input"
                      type="file"
                      accept=".pdf"
                      className="d-none"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handlePDFUpload(file);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="row g-4">
            {/* Image Panel */}
            <div className="col-lg-3">
              <div className="card shadow h-100">
                <div className="card-body">
                  <h5 className="card-title d-flex align-items-center mb-4">
                    <Signature size={20} className="me-2 ml-1" />
                    Signature
                  </h5>

                  <div className="mb-4">
                    <div
                      className="drop-zone rounded p-4 text-center bg-light"
                      onDrop={(e) => handleDrop(e, "image")}
                      onDragOver={(e) => e.preventDefault()}
                      onClick={() =>
                        document.getElementById("image-input")?.click()
                      }
                    >
                      <div className="d-flex flex-column align-items-center">
                        <Upload size={32} className="text-muted mb-2" />
                        <h6 className="fw-bold text-dark mb-1">
                          Add Signature
                        </h6>
                        <small className="text-muted">PNG, JPG, JPEG</small>
                      </div>
                      <input
                        id="image-input"
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const img = new Image();
                            img.onload = () => {
                              URL.revokeObjectURL(img.src);

                              handleImageUpload(
                                file,
                                img.naturalWidth,
                                img.naturalHeight
                              );
                            };
                            img.src = URL.createObjectURL(file);
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="row g-2">
                    {uploadedImages.map((image, index) => (
                      <div
                        key={index}
                        className="col-5 mx-2 mt-1 border border-b-orange-100  position-relative"
                      >
                        <div
                          className="bg-light rounded  position-relative"
                          style={{ aspectRatio: "1" }}
                        >
                          <img
                            src={
                              image.isUrl
                                ? image.file
                                : URL.createObjectURL(image.file)
                            }
                            alt={`Uploaded ${index + 1}`}
                            className="image-thumbnail w-100 h-100 border-0"
                            onClick={() => {
                              addImageToPDF(
                                image?.file,
                                image?.width,
                                image?.height
                              );
                            }}
                          />
                          <button
                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeUploadedImage(index, image);
                            }}
                            title="Delete image"
                            style={{ fontSize: "10px" }}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="col-5 mx-2 mt-1 border border-b-orange-100  position-relative">
                      <div
                        className="bg-light rounded  position-relative"
                        style={{ aspectRatio: "1" }}
                      >
                        <img
                          className="image-thumbnail w-100 h-100 border-0"
                          onClick={async () => {
                            const file = await fetchImageAsFile(
                              process.env.REACT_APP_ENV +
                              "/assets/images/icons/action/Sign.png",
                              "signature_h.png"
                            );

                            addImageToPDF(file, 140, 120);
                          }}
                          src={
                            process.env.REACT_APP_ENV +
                            "/assets/images/icons/action/Sign.png"
                          }
                          alt="alto"
                        />

                        {/* <img
                            className="image-thumbnail w-100 h-100 border-0"
                            onClick={(e) => {
                              console.log(e,"e")
                            }}
                            src={
                              process.env.REACT_APP_ENV +
                              "/assets/images/icons/action/Sign.png"
                            }
                            alt="alto"

                          /> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* PDF Viewer */}
            <div className="col-lg-9">
              <div className="card shadow h-100">
                {/* Toolbar */}
                <div className="toolbar d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    <button
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage <= 1}
                      className="btn btn-outline-secondary"
                    >
                      <ChevronLeft size={20} />
                    </button>

                    <span className="fw-bold p-2">
                      Page {currentPage} of {pdfInfo.numPages}
                    </span>

                    <button
                      onClick={() =>
                        setCurrentPage(
                          Math.min(pdfInfo.numPages, currentPage + 1)
                        )
                      }
                      disabled={currentPage >= pdfInfo.numPages}
                      className="btn btn-outline-secondary"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <button
                      onClick={() => setScale(Math.max(0.5, scale - 0.1))}
                      className="btn btn-outline-secondary"
                    >
                      <ZoomOut size={20} />
                    </button>

                    <span className="fw-bold p-2">
                      {Math.round(scale * 100)}%
                    </span>
                    {(referenceNos || sequenceNos || fileUrl) && (
                      <button
                        onClick={() => savePDFWithImages("API")}
                        className="btn btn-primary d-flex align-items-center gap-2 ml-2"
                      >
                        <Download size={16} />
                        <span>Save PDF</span>
                      </button>
                    )}

                    <button
                      onClick={() => savePDFWithImages("DOWNLOAD")}
                      className="btn btn-primary d-flex align-items-center gap-2 ml-2"
                    >
                      <Download size={16} />
                      <span>Download</span>
                    </button>
                  </div>
                </div>

                {/* PDF Viewer */}
                <div className="pdf-viewer-container">
                  <div className="pdf-container">
                    <div className="pdf-page-container" ref={pdfContainerRef}>
                      <Document
                        file={pdfInfo.url}
                        onLoadSuccess={handlePDFLoad}
                        loading={
                          <div
                            className="d-flex align-items-center justify-content-center bg-white rounded shadow"
                            style={{ height: "400px" }}
                          >
                            <div className="text-center">
                              <div
                                className="loading-spinner border border-primary border-3"
                                style={{
                                  width: "3rem",
                                  height: "3rem",
                                  borderTopColor: "transparent",
                                }}
                              ></div>
                              <p className="text-muted mt-3">Loading PDF...</p>
                            </div>
                          </div>
                        }
                        error={
                          <div
                            className="d-flex align-items-center justify-content-center bg-white rounded shadow"
                            style={{ height: "400px" }}
                          >
                            <div className="text-center">
                              <p className="text-danger mb-3">
                                Error loading PDF
                              </p>
                              <button
                                onClick={() => window.location.reload()}
                                className="btn btn-primary"
                              >
                                Try Again
                              </button>
                            </div>
                          </div>
                        }
                      >
                        <Page
                          pageNumber={currentPage}
                          scale={scale}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          className="shadow"
                          onRenderSuccess={handlePageRenderSuccess}
                        />
                      </Document>

                      {/* Placed Images */}
                      {/* Placed Images */}
                      {currentPageImages.map((image) => (
                        <div
                          key={image.id}
                          className="placed-image"
                          style={{
                            left: image.x * scale,
                            top: image.y * scale,
                            width: image.width * scale,
                            height: image.height * scale,
                            cursor:
                              draggedImage === image.id ? "grabbing" : "grab",
                          }}
                          onMouseDown={(e) =>
                            handleMouseDown(e, image.id, "drag")
                          }
                        >
                          <img
                            src={image.url}
                            alt="Placed image"
                            className="w-100 h-100"
                            style={{
                              objectFit: "fill",
                              pointerEvents: "none",
                              userSelect: "none",
                            }}
                            draggable={false}
                          />
                          {image.timestamp && (
                            <div
                              style={{
                                position: "absolute",
                                bottom: -50, // slightly below the signature
                                left: 2,
                                fontSize: "10px",
                                color: "black",
                                background: "rgba(255,255,255,0.7)", // makes text readable
                                padding: "1px 3px",
                                borderRadius: "3px",
                              }}
                            >
                              <div className=""> {` ${image.userName}`} </div>
                              <div className=""> {`${image.userType}`} </div>
                              <div> {`Signed at: ${image.timestamp}`} </div>
                              {/* {`Signed at: ${image.timestamp}`}
                              {`Signed at: ${image.timestamp}`} */}
                              { }
                            </div>
                          )}

                          {/* Control buttons */}
                          <div className="image-controls">
                            <button
                              className="btn btn-primary btn-sm"
                              title="Move"
                              style={{ fontSize: "10px", padding: "2px 4px" }}
                            >
                              <Move size={12} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removePlacedImage(image.id);
                              }}
                              className="btn btn-danger btn-sm"
                              title="Remove"
                              style={{ fontSize: "10px", padding: "2px 4px" }}
                            >
                              <X size={12} />
                            </button>
                          </div>

                          {/* Resize handle */}
                          <div
                            className="resize-handle"
                            onMouseDown={(e) =>
                              handleMouseDown(e, image.id, "resize")
                            }
                            style={{ cursor: "se-resize" }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
const mapStateToProps = (state) => {
  return {
    menuVisibility: state.menuVisibility,
    menuStatusHeader: state.menuStatusHeader,
    getToken: state.token,
    userType: state.userType,
    userName: state.userName,
    UserId: state.UserId,
    designationName: state.designationName,
  };
};

export default connect(mapStateToProps)(SignaturePage);


